import { PositionDetails } from "@pancakeswap/farms"
import { useTranslation } from "@pancakeswap/localization"
import { Balance, Box, Button, Flex, FlexGap, Heading, IconButton, InjectedModalProps, Modal, ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalTitle, PencilIcon, RowBetween, Slider, SyncAltIcon, Text, TooltipText, useModal, useTooltip } from "@pancakeswap/uikit"
import { ADDRESS_ZERO, FeeAmount, MaxUint128, NonfungiblePositionManager, Payments, Pool, Position, TICK_SPACINGS, TickMath, tickToPrice } from "@pancakeswap/v3-sdk"
import { CurrencyLogo } from "components/Logo"
import { useCurrency } from "hooks/Tokens"
import { useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { PositionData } from "views/V3Info/types"
import { Currency, CurrencyAmount, Percent } from "@pancakeswap/swap-sdk-core"
import useTransactionDeadline from "hooks/useTransactionDeadline"
import { useUserSlippage } from "@pancakeswap/utils/user"
import SettingsModal from "components/EMenu/GlobalSettings/SettingsModal"
import { SettingsMode } from "components/EMenu/GlobalSettings/types"
import { formatAmount } from "utils/formatCurrencyAmount"
import { usePool } from "hooks/v3/usePools"
import ConnectWalletButton from "components/ConnectWalletButton"
import useActiveWeb3React from "hooks/useActiveWeb3React"
import { useV3NFTPositionManagerContract } from "hooks/useContract"
import TransactionConfirmationModal from "components/TransactionConfirmationModal"
import { basisPointsToPercent } from "utils/exchange"
import { useV3PositionFees } from "hooks/v3/useV3PositionFees"
import { encodeFunctionData, formatUnits, hexToBigInt, parseEther, parseUnits } from "viem"
import useCatchTxError from "hooks/useCatchTxError"
import { calculateGasMargin } from "utils"
import { useBUSDCurrencyAmount } from "hooks/useBUSDPrice"
import { PoolData } from "../hooks/usePools"

const SliderContainer = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  background: #2B2634;
  border-radius: 16px;
  padding: 16px 20px;
  
  @media screen and (max-width: 480px) {
    gap: 4px;
  }
`

const SliderRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  
  @media screen and (max-width: 480px) {
    gap: 8px;
  }
`

const ButtonGrid = styled(Flex)`
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 4px;
  background: #231F2B;
  padding: 4px;
  border-radius: 12px;
  
  @media screen and (max-width: 480px) {
    gap: 4px;
  }
`

const PercentageButton = styled(Button).attrs({ scale: "sm" }) <{ selected: boolean }>`
  font-size: 15px;
  flex: 1;
  padding: 8px;
  height: 38px;
  border-radius: 8px;
  background: ${({ selected }) => selected ? '#49415A' : 'transparent'};
  box-shadow: none;
`

const Hr = styled.hr`
  width: 100%;
  border-color: #FFFFFF17;
  border-width: 1px 0 0 0;
  margin: 1em 0;
`

const TokenId = styled(Text)`
  background: #3E3A46;
  border-radius: 16px;
  padding: 2px 1em;
`

const InRange = styled(Text) <{ valid: boolean }>`
  border-radius: 8px;
  padding: 5px 5px;
  font-size: 12px;
  font-weight: bold;
  line-height: 12px;
  white-space: nowrap;
  background: ${({ theme, valid }) => valid ? '#2EBF43' : theme.colors.textSubtle};
  color: ${({ theme }) => theme.colors.backgroundAlt};
`

interface DecreaseModalProps extends InjectedModalProps {
  currencyA: Currency
  currencyB: Currency
  positions: PositionData[]
  poolData: PoolData
  // positionDetails: PositionDetails
  // poolStats: PoolData
  // liquidityUSD: number
  // currentPrice: number
  // token0Percentage: number
  // token1Percentage: number
}

const isFullrange = (position: PositionData, tickSpacing: number) => {
  if (!tickSpacing)
    return true
  const tickLower = Math.round(TickMath.MIN_TICK / tickSpacing) * tickSpacing
  const tickUpper = Math.floor(TickMath.MAX_TICK / tickSpacing) * tickSpacing
  return tickLower === position.tickLower && tickUpper === position.tickUpper
}

const PositionSlider = ({ currencyA, currencyB, position, pool, updateResult }: { currencyA: Currency, currencyB: Currency, position: PositionData, pool: Pool, updateResult: (tokenId: string, percent: number) => void }) => {
  const [percent, setPercent] = useState(0)
  const [inverted, setInvert] = useState(false)

  const [currency0, currency1] = inverted ? [currencyB, currencyA] : [currencyA, currencyB]
  const priceLower = tickToPrice(currency0.wrapped, currency1.wrapped, inverted ? position.tickUpper : position.tickLower)
  const priceUpper = tickToPrice(currency0.wrapped, currency1.wrapped, inverted ? position.tickLower : position.tickUpper)

  const fullRange = isFullrange(position, TICK_SPACINGS[pool?.fee])
  const inRange = position.tick >= position.tickLower && position.tick <= position.tickUpper

  // const [feeValue0, feeValue1] = useV3PositionFees(pool, position.tokenId, false)

  const updatePercent = (p) => {
    setPercent(p)
    updateResult(String(position.tokenId), p)
  }

  return (
    <SliderContainer width={['100%', , '500px']}>
      <FlexGap gap="8px" alignItems="flex-end">
        <TokenId fontSize={14}>Position #{position.tokenId.toString()}</TokenId>
        <InRange valid={inRange}>{inRange ? 'In Range' : 'Out of Range'}</InRange>
      </FlexGap>
      <FlexGap gap="8px" alignItems="baseline" pl={2} mt={1}>
        <Text fontSize={[12, 16]} bold>{fullRange ? '0' : priceLower.toSignificant(8)} - {fullRange ? '∞' : priceUpper.toSignificant(8)}</Text>
        <Text fontSize={[12, 14]} color="textSubtle">{currency1.symbol} per {currency0.symbol}</Text>
        <SyncAltIcon color="textSubtle" onClick={() => setInvert(!inverted)} />
      </FlexGap>
      <Hr style={{ margin: 0 }} />
      <div>
        <SliderRow mb={2}>
          <Box style={{ flex: 1 }}>
            <Slider
              name="lp-amount"
              min={0}
              max={100}
              value={percent}
              onValueChanged={(v: number) => updatePercent(Math.ceil(v))}
            />
          </Box>
          <Text fontSize={32} minWidth="3em" textAlign="right">
            {percent}%
          </Text>
        </SliderRow>
        <ButtonGrid>
          <PercentageButton selected={percent === 25} onClick={() => updatePercent(25)}>
            25%
          </PercentageButton>
          <PercentageButton selected={percent === 50} onClick={() => updatePercent(50)}>
            50%
          </PercentageButton>
          <PercentageButton selected={percent === 75} onClick={() => updatePercent(75)}>
            75%
          </PercentageButton>
          <PercentageButton selected={percent === 100} onClick={() => updatePercent(100)}>
            Max
          </PercentageButton>
        </ButtonGrid>
      </div>
    </SliderContainer>
  )
}

const DecreaseModal: React.FC<DecreaseModalProps> = ({
  currencyA,
  currencyB,
  positions,
  poolData,
  onDismiss
}) => {
  const { t } = useTranslation()

  const { account, chainId, isWrongNetwork } = useActiveWeb3React()

  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [percents, setPercents] = useState<{ [tokenId: string]: number }>({})
  // const [fees, setFees] = useState<{ [tokenId: string]: [bigint, bigint] }>({})

  // const [parsedAmount, setParsedAmount] = useState<bigint>()
  // const [formattedAmount, setFormattedAmount] = useState<string>()

  const positionManager = useV3NFTPositionManagerContract()

  const [, pool] = usePool(currencyA, currencyB, poolData.feeTier * 10000)

  const { token0Recieve, token1Recieve, token0USD, token1USD, token0Fee, token1Fee, token0FeeUSD, token1FeeUSD } = useMemo(() => {
    return positions.reduce((summary, position) => {
      const percent = percents[String(position.tokenId)]
      if (percent) {
        // if (pool) {
        //   const partialPosition = new Position({
        //     pool,
        //     liquidity: new Percent(Math.floor(percent * 10000), 1000000).multiply(position.liquidity).quotient,
        //     tickLower: position.tickLower,
        //     tickUpper: position.tickUpper,
        //   })
        //   const { amount0, amount1 } = partialPosition.burnAmountsWithSlippage(
        //     new Percent(0, 100)
        //   )
        //   return {
        //     token0Recieve: summary.token0Recieve + Number(formatUnits(amount0, pool.token0.decimals)),
        //     token1Recieve: summary.token1Recieve + Number(formatUnits(amount1, pool.token1.decimals)),
        //     token0USD: summary.token0USD + position.token0USD * percent / 100,
        //     token1USD: summary.token1USD + position.token1USD * percent / 100,
        //     token0Fee: summary.token0Fee + position.feeToken0,
        //     token1Fee: summary.token1Fee + position.feeToken1,
        //     token0FeeUSD: summary.token0FeeUSD + position.fee0USD,
        //     token1FeeUSD: summary.token1FeeUSD + position.fee1USD,
        //   }
        // }
        return {
          token0Recieve: summary.token0Recieve + position.liquidityToken0 * percent / 100,
          token1Recieve: summary.token1Recieve + position.liquidityToken1 * percent / 100,
          token0USD: summary.token0USD + position.token0USD * percent / 100,
          token1USD: summary.token1USD + position.token1USD * percent / 100,
          token0Fee: summary.token0Fee + position.feeToken0,
          token1Fee: summary.token1Fee + position.feeToken1,
          token0FeeUSD: summary.token0FeeUSD + position.fee0USD,
          token1FeeUSD: summary.token1FeeUSD + position.fee1USD,
        }
      }
      return summary
    }, {
      token0Recieve: 0,
      token1Recieve: 0,
      token0USD: 0,
      token1USD: 0,
      token0Fee: 0,
      token1Fee: 0,
      token0FeeUSD: 0,
      token1FeeUSD: 0
    })
  }, [percents, positions])

  const totalPercent = Object.values(percents).reduce((sum, p) => sum + p, 0)

  const deadline = useTransactionDeadline()
  const [allowedSlippage] = useUserSlippage()

  // const { targetRef, tooltip, tooltipVisible } = useTooltip(
  //   t(`Based on last 7 days' performance. Does not account for impermanent loss`),
  //   {
  //     placement: 'bottom',
  //   },
  // )

  // const { toastRejection } = useCustomToast()

  const [onPresentSettingsModal] = useModal(<SettingsModal mode={SettingsMode.SWAP_LIQUIDITY} />)

  const { fetchTxResponse } = useCatchTxError()

  const onRemove = useCallback(async () => {
    if (!positionManager)
      return
    setAttemptingTxn(true)
    const feeAmounts = await Promise.all(positions.map(p =>
      positionManager.simulate.collect([
        {
          tokenId: p.tokenId,
          recipient: account, // some tokens might fail if transferred to address(0)
          amount0Max: MaxUint128,
          amount1Max: MaxUint128,
        },
      ], { account, value: 0n }).then(results => results.result)
    ))
    const slippageTolerance = basisPointsToPercent(allowedSlippage)
    const involvesETH = currencyA.isNative || currencyB.isNative
    const calldata = positions.flatMap((position, i) => {
      if (!percents[String(position.tokenId)])
        return undefined
      const percent = new Percent(Math.floor(percents[String(position.tokenId)] * 10000), 1000000)
      if (percent.equalTo(0))
        return undefined
      const partialPosition = new Position({
        pool,
        liquidity: percent.multiply(position.liquidity).quotient,
        tickLower: position.tickLower,
        tickUpper: position.tickUpper,
      })
      const { amount0: amount0Min, amount1: amount1Min } = partialPosition.burnAmountsWithSlippage(
        slippageTolerance
      )
      const calldatas = []
      calldatas.push(encodeFunctionData({
        abi: NonfungiblePositionManager.ABI,
        functionName: 'decreaseLiquidity',
        args: [
          {
            tokenId: position.tokenId,
            liquidity: partialPosition.liquidity,
            amount0Min,
            amount1Min,
            deadline,
          },
        ],
      }))
      const [fee0, fee1] = feeAmounts[i]
      calldatas.push(
        encodeFunctionData({
          abi: NonfungiblePositionManager.ABI,
          functionName: 'collect',
          args: [
            {
              tokenId: position.tokenId,
              recipient: involvesETH ? ADDRESS_ZERO : account,
              amount0Max: MaxUint128,
              amount1Max: MaxUint128,
            },
          ],
        })
      )
      if (involvesETH) {
        if (currencyA.isNative)
          calldatas.push(Payments.encodeUnwrapWETH9(fee0 + amount0Min, account))
        else
          calldatas.push(Payments.encodeSweepToken(currencyA.wrapped, fee0 + amount0Min, account))
        if (currencyB.isNative)
          calldatas.push(Payments.encodeUnwrapWETH9(fee1 + amount1Min, account))
        else
          calldatas.push(Payments.encodeSweepToken(currencyB.wrapped, fee1 + amount1Min, account))
      }
      if (percent.equalTo(100)) {
        calldatas.push(
          encodeFunctionData({ abi: NonfungiblePositionManager.ABI, functionName: 'burn', args: [position.tokenId] })
        )
      }
      return calldatas
    }).filter(Boolean)
    positionManager.estimateGas.multicall([calldata], {}).then(gas =>
      fetchTxResponse(() =>
        positionManager.write.multicall(
          [calldata],
          { gas: calculateGasMargin(gas) }
        ), {
        summary: "Removed",
        translatableSummary: {
          text: 'Removed %baseAmount% %baseSymbol% and %quoteAmount% %quoteSymbol%',
          data: {
            baseAmount: token0Recieve.toFixed(2),
            baseSymbol: currencyA.symbol,
            quoteAmount: token1Recieve.toFixed(2),
            quoteSymbol: currencyB.symbol,
            token0Address: currencyA.wrapped.address,
            token1Address: currencyB.wrapped.address,
          },
        },
        type: 'remove-liquidity-v3',
      }).catch((e) => {
        console.log('error', e)
      })
    ).then(() => onDismiss?.()).finally(() => setAttemptingTxn(false))
  }, [positionManager, allowedSlippage, positions, fetchTxResponse, onDismiss, token0Recieve, currencyA, token1Recieve, currencyB, percents, pool, deadline, account])

  const handleDismiss = useCallback(() => {
    onDismiss?.()
  }, [onDismiss])

  const translationData = useMemo(
    () => ({
      amountA: formatAmount(token0Recieve),
      symbolA: currencyA.symbol,
      amountB: formatAmount(token1Recieve),
      symbolB: currencyB.symbol,
    }),
    [currencyA, currencyB, token0Recieve, token1Recieve],
  )

  const pendingText = useMemo(
    () =>
      t('Removing %amountA% %symbolA% %amountB% %symbolB%', translationData),
    [t, translationData],
  )

  const amountInDollarA = useBUSDCurrencyAmount(
    currencyA,
    Number.isFinite(+token0Recieve) ? +token0Recieve : undefined,
  )

  const amountInDollarB = useBUSDCurrencyAmount(
    currencyB,
    Number.isFinite(+token1Recieve) ? +token1Recieve : undefined,
  )

  const [onPresentConfirmModal] = useModal(
    <TransactionConfirmationModal
      minWidth={['100%', null, '420px']}
      title={t('Remove Liquidity')}
      // customOnDismiss={}
      attemptingTxn={attemptingTxn}
      hash={''}
      content={() => null}
      pendingText={pendingText}
    />,
    false,
    true,
    `confirm-remove-${poolData.address}`,
  )

  const handleButtonSubmit = () => {
    onPresentConfirmModal()
    onRemove()
  }

  return (
    <Modal title={t("Remove Liquidity")} onDismiss={handleDismiss}>
      <FlexGap gap="8px" flexDirection="column">
        {
          positions.map(position =>
            <PositionSlider
              key={`position-${position.tokenId}`}
              currencyA={currencyA}
              currencyB={currencyB}
              position={position}
              pool={pool}
              updateResult={(tokenId, percent) => {
                setPercents((p) => ({ ...p, [tokenId]: percent }))
              }} />
          )
        }
      </FlexGap>
      <FlexGap gap="24px" flexDirection={["column", "column", "row"]} mt={4}>
        <FlexGap flexDirection="column" flex={1}>
          <Text fontSize={15} mb="8px">
            {t('Receive')}
          </Text>
          <FlexGap gap="8px" alignItems="center">
            <CurrencyLogo currency={currencyA} size="17px" />
            <Text color="textSubtle" fontSize={16}>
              {currencyA?.symbol}
            </Text>
            <Balance value={Number(token0Recieve || '0')} decimals={4} fontSize={12} ml="auto" />
          </FlexGap>
          <Text color="textSubtle" fontSize={12} textAlign="right">
            ${token0USD.toFixed(2)}
          </Text>
          <FlexGap gap="8px" alignItems="center">
            <CurrencyLogo currency={currencyB} size="17px" />
            <Text color="textSubtle" fontSize={16}>
              {currencyB?.symbol}
            </Text>
            <Balance value={Number(token1Recieve || '0')} decimals={4} fontSize={12} ml="auto" />
          </FlexGap>
          <Text color="textSubtle" fontSize={12} textAlign="right">
            ${token1USD.toFixed(2)}
          </Text>
        </FlexGap>
        <FlexGap flexDirection="column" flex={1}>
          <Text fontSize={15} mb="8px">
            {t('Fees')}
          </Text>
          <FlexGap gap="8px" alignItems="center">
            <CurrencyLogo currency={currencyA} size="17px" />
            <Text color="textSubtle" fontSize={16}>
              {currencyA?.symbol}
            </Text>
            <Balance value={Number(token0Fee || '0')} decimals={4} fontSize={12} ml="auto" />
          </FlexGap>
          <Text color="textSubtle" fontSize={12} textAlign="right">
            ${token0FeeUSD.toFixed(2)}
          </Text>
          <FlexGap gap="8px" alignItems="center">
            <CurrencyLogo currency={currencyB} size="17px" />
            <Text color="textSubtle" fontSize={16}>
              {currencyB?.symbol}
            </Text>
            <Balance value={Number(token1Fee || '0')} decimals={4} fontSize={12} ml="auto" />
          </FlexGap>
          <Text color="textSubtle" fontSize={12} textAlign="right">
            ${token1FeeUSD.toFixed(2)}
          </Text>
        </FlexGap>
      </FlexGap>
      {/* {
        !!pool &&
        <>
          <Hr />
          <FlexGap flexDirection="column" gap="8px">
            <Text fontSize={15}>
              {t('Prices')}
            </Text>
            <FlexGap gap="8px">
              <Text color="textSubtle" fontSize={12}>
                1 {currencyB?.symbol} =
              </Text>
              <Text fontSize={12} ml="auto">
                {formatAmount(pool.priceOf(currencyB.wrapped).toSignificant(6))} {currencyA?.symbol}
              </Text>
            </FlexGap>
            <FlexGap gap="8px">
              <Text color="textSubtle" fontSize={12}>
                1 {currencyA?.symbol} =
              </Text>
              <Text fontSize={12} ml="auto">
                {formatAmount(pool.priceOf(currencyA.wrapped).toSignificant(6))} {currencyB?.symbol}
              </Text>
            </FlexGap>
          </FlexGap>
        </>
      } */}
      <Hr />
      <FlexGap gap="8px" justifyContent="space-between" alignItems="center">
        <Text color="textSubtle" fontSize={12}>
          {t('Slippage Tolerance')}
          <IconButton scale="sm" variant="text" onClick={onPresentSettingsModal}>
            <PencilIcon color="textSubtle" width="10px" />
          </IconButton>
        </Text>
        <Text fontSize={12}>
          {allowedSlippage / 100}%
        </Text>
      </FlexGap>
      {/* {poolData && (
        <FlexGap gap="8px" justifyContent="space-between" alignItems="center">
          <TooltipText ref={targetRef} fontSize="12px" color="textSubtle">
            {t('LP reward APR')}
          </TooltipText>
          {tooltipVisible && tooltip}
          <Balance fontSize={12} value={poolData.apr7d} unit="%" decimals={2} />
        </FlexGap>
      )} */}
      <Box mt="16px">
        {!account ? (
          <ConnectWalletButton width="100%" style={{ minHeight: '72px' }} />
        ) : (
          <Button
            onClick={handleButtonSubmit}
            width="100%"
            style={{ minHeight: '72px' }}
            disabled={!totalPercent}
          >
            {t('Remove')}
          </Button>
        )}
      </Box>
    </Modal>
  )
}

export default DecreaseModal
